// The WORK CARD surface — the routed, deep-linkable decision page (brief §5.3; verdict email links
// land here). Anatomy in the binding block order: header (subject, state pill, tags, evidence
// as-of), decision blocks with STAGING ticks + the ONE Execute bar (or the honest no-open-round
// note), the selected proposal's frozen payload, the automatic follow-on, evidence blocks,
// reasoning, item history. Thin over services: it fetches the card projection through the injected
// WorkApi, routes the ONE Execute commit (no approval field — P-GATE) and RE-READS after every
// accepted or conflicted commit so it renders what the owning module recorded (G-182 — never
// optimistic; on verdict_conflict it re-reads, never retries). Verdict controls render only where
// the projection's `canRule` is true — rendering only, the owning module is the boundary (G-180).
// Tokens only (G-181); no internal identifier on the surface (G-197).

import { useCallback } from 'react';
import { useWorkApi } from '../api-client.ts';
import { useProjection } from '../../useProjection.ts';
import { SurfaceFrame } from '../../surface-frame.tsx';
import { StatusPill } from '../../primitives.tsx';
import { AsOfPill, WorkMiniBadge, WorkSectionHead, WorkTag } from '../work-ui.tsx';
import type { WorkCardSurfaceProps } from '../surface-contracts.ts';
import type { DecisionBlockView, StagedVerdictWire, WorkCardProjection } from '../projections.ts';
import { DecisionPanel } from './DecisionPanel.tsx';
import { EvidenceList } from './EvidenceList.tsx';
import { HistoryList } from './HistoryList.tsx';
import { openOptionsOf } from './staging.ts';

/** The card head: back crumb, subject, figure, state pill, tags, evidence as-of (brief §5.3.1). */
function CardHeader({
  card,
  onBack,
}: {
  readonly card: WorkCardProjection;
  readonly onBack: () => void;
}): React.JSX.Element {
  const item = card.item;
  const stale = card.evidence.some((block) => block.stale);
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        aria-label={`Back to ${card.definitionName}`}
        className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle hover:text-forest"
      >
        ← {card.definitionName}
      </button>
      <h1 className="ritona-voice mt-3 text-2xl font-medium tracking-snug text-text">
        {item.subjectDisplay}
      </h1>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {item.figure !== null ? (
          <span className="ritona-voice text-lg text-text">{item.figure}</span>
        ) : null}
        {item.figureNote !== null ? (
          <span className="ritona-numeric text-xs text-text-subtle">{item.figureNote}</span>
        ) : null}
        <span data-state={item.state}>
          <StatusPill tone="neutral">{item.state}</StatusPill>
        </span>
        {item.tags.map((tag) => (
          <WorkTag key={tag} label={tag} />
        ))}
        <AsOfPill asOf={item.evidenceAsOf} stale={stale} />
        {item.attentionLabel !== null ? (
          <WorkMiniBadge kind="stop">{item.attentionLabel}</WorkMiniBadge>
        ) : null}
      </div>
    </div>
  );
}

/** The honest empty round (brief §5.3.2: decisions omitted — evidence + history only). */
function NoRoundNote(): React.JSX.Element {
  return (
    <div
      data-no-round="true"
      className="mt-8 rounded-md border border-dashed border-border bg-surface-sunken px-4 py-4 text-sm text-text-muted"
    >
      No open decision this round — Ritona is watching this item. The card is evidence and history
      only until the state or the evidence moves.
    </div>
  );
}

/** The worker's reasoning, one summary per open proposal (agent voice — brief §5.3.5). */
function ReasoningSection({
  decisions,
}: {
  readonly decisions: readonly DecisionBlockView[];
}): React.JSX.Element | null {
  const open = decisions.flatMap((block) => openOptionsOf(block));
  if (open.length === 0) return null;
  return (
    <section aria-label="Reasoning" className="mt-8">
      <WorkSectionHead label="Reasoning" source="the worker’s summary, per proposal" />
      <div className="flex flex-col gap-4">
        {open.map((option) => (
          <div key={option.id}>
            <div className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle">
              {option.title}
            </div>
            <p className="ritona-voice mt-1 max-w-prose text-base leading-relaxed text-text">
              {option.reasoningSummary}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/** The routed Work card page: stage ticks, ONE Execute commit, then re-read (brief §3.5/§5.3). */
export function WorkCardSurface({ itemId, onBack }: WorkCardSurfaceProps): React.JSX.Element {
  const api = useWorkApi();
  const loadCard = useCallback(() => api.getCard(itemId), [api, itemId]);
  const state = useProjection<WorkCardProjection>(loadCard);
  const reload = state.reload;

  // Route the ONE commit; re-read on ok AND on a conflicted round (never optimistic, never retry).
  const onExecute = useCallback(
    async (staged: readonly StagedVerdictWire[]) => {
      const result = await api.execute(itemId, staged);
      const conflicted =
        result.conflict ||
        result.outcomes.some(
          (row) => row.outcome === 'verdict_conflict' || row.outcome === 'inconsistent_staging',
        );
      if (result.ok || conflicted) reload();
      return result;
    },
    [api, itemId, reload],
  );

  return (
    <SurfaceFrame state={state}>
      {(card) => (
        <div className="mx-auto max-w-3xl">
          <CardHeader card={card} onBack={onBack} />
          {card.decisions.length > 0 ? (
            <DecisionPanel
              decisions={card.decisions}
              itemState={card.item.state}
              canRule={card.item.canRule}
              onExecute={onExecute}
            />
          ) : (
            <NoRoundNote />
          )}
          <EvidenceList evidence={card.evidence} />
          <ReasoningSection decisions={card.decisions} />
          <HistoryList history={card.history} />
        </div>
      )}
    </SurfaceFrame>
  );
}
